/**
 * PSE per-host timeout circuit breaker.
 *
 * The sentiment sub-providers issue MANY requests per orchestrator run
 * (per query × per BTM tech × per geographic scope). `fetchWithTimeout`
 * already bounds each request, but a DEAD host re-earned its full
 * timeout on every request: observed 2026-07-28 on Redhead, municode
 * burned 15s × 10+ requests and regulations.gov 20s × 4 in one topup,
 * pushing the section past its budget while returning nothing.
 *
 * After N consecutive TIMEOUTS for one host key, the circuit opens and
 * further requests fail fast with `PseCircuitOpenError`. Any successful
 * response closes the circuit. Non-timeout failures (HTTP errors,
 * caller aborts) never open it — HTTP-level circuits stay the
 * responsibility of each provider (e.g. the FERC 429 circuit).
 *
 * Session-scoped: the orchestrator resets all circuits at the start of
 * each run (`resetPseFetchCircuits`, beside
 * `resetFederalDocketsFetchSession`), so one bad run never poisons the
 * next.
 */
import { fetchWithTimeout } from './types';

const OPEN_AFTER_CONSECUTIVE_TIMEOUTS = 2;

const consecutiveTimeouts = new Map<string, number>();

export class PseCircuitOpenError extends Error {
  constructor(hostKey: string, timeouts: number) {
    super(`${hostKey}: circuit open after ${timeouts} consecutive timeouts — failing fast for the rest of this run`);
    this.name = 'PseCircuitOpenError';
  }
}

/** Reset all host circuits — call at the start of each orchestrator run. */
export function resetPseFetchCircuits(): void {
  consecutiveTimeouts.clear();
}

/** True for the abort reason `fetchWithTimeout` raises on ITS OWN timer. */
export function isPseTimeoutError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return message.includes('timed out after');
}

/**
 * `fetchWithTimeout` behind the per-host circuit. `hostKey` groups
 * requests that share upstream fate (one key per vendor, not per URL).
 * `fetcher` is injectable for tests only.
 */
export async function fetchWithTimeoutCircuit(
  hostKey: string,
  url: string,
  timeoutMs: number,
  signal?: AbortSignal,
  fetcher: typeof fetchWithTimeout = fetchWithTimeout,
): Promise<Response> {
  const timeouts = consecutiveTimeouts.get(hostKey) ?? 0;
  if (timeouts >= OPEN_AFTER_CONSECUTIVE_TIMEOUTS) {
    throw new PseCircuitOpenError(hostKey, timeouts);
  }
  try {
    const res = await fetcher(url, timeoutMs, signal);
    consecutiveTimeouts.set(hostKey, 0);
    return res;
  } catch (error) {
    // A caller-initiated abort is not the host's fault.
    if (isPseTimeoutError(error) && !signal?.aborted) {
      consecutiveTimeouts.set(hostKey, timeouts + 1);
    }
    throw error;
  }
}
